import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/shared/Modal';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Icon } from '../../components/shared/Icons';
import { unpublishClip } from '../../services/clips.service';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess, notifyWarning } from '../../lib/toast';
import { invalidateClipCaches } from '../../hooks/use-clips';

interface BulkUnpublishClipsDialogProps {
  open: boolean;
  onClose: () => void;
  // The selection bar pre-filters to currently-published clips (it's the only thing that
  // makes sense to unpublish), so this list is "the live clips the user wants pulled down".
  clips: { id: string; title: string }[];
  onDone?: (result: { succeededIds: string[]; failedIds: string[] }) => void;
}

// Bulk unpublish confirm dialog. Same fan-out pattern as BulkDeleteClipsDialog but with
// the unpublish endpoint, which is idempotent server-side — a clip that flipped to draft
// in another tab between the click and the request will just return 200 unchanged.
export function BulkUnpublishClipsDialog({ open, onClose, clips, onDone }: BulkUnpublishClipsDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);

  const HEAD = 5;
  const head = clips.slice(0, HEAD);
  const restCount = Math.max(0, clips.length - HEAD);

  async function onConfirm() {
    setRunning(true);
    try {
      const results = await Promise.allSettled(clips.map((c) => unpublishClip(c.id)));
      const succeededIds: string[] = [];
      const failedIds: string[] = [];
      let firstError: unknown = null;
      results.forEach((r, i) => {
        const id = clips[i]?.id;
        if (!id) return;
        if (r.status === 'fulfilled') {
          succeededIds.push(id);
          // Invalidate the single-clip query so the editor reflects the new state on next
          // visit — we DON'T removeQueries here (unlike delete), because the clip still exists.
          void qc.invalidateQueries({ queryKey: ['clip', id] });
        } else {
          failedIds.push(id);
          firstError ??= r.reason;
        }
      });

      // Sweep every clip cache, not just the admin table — an unpublished clip has to leave the
      // referee library too, and that's a different set of query keys.
      invalidateClipCaches(qc);

      if (failedIds.length === 0) {
        notifySuccess(t('admin.clips.bulkUnpublishDialog.successToast', { count: succeededIds.length }));
      } else if (succeededIds.length === 0) {
        notifyError(firstError, t('admin.clips.bulkUnpublishDialog.allFailedToast', { count: failedIds.length }));
      } else {
        notifyWarning(
          t('admin.clips.bulkUnpublishDialog.partialToast', {
            succeeded: succeededIds.length,
            failed: failedIds.length,
            reason: apiErrorMessage(firstError, 'see logs'),
          }),
        );
      }

      onDone?.({ succeededIds, failedIds });
      onClose();
    } finally {
      setRunning(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={running ? () => {} : onClose}
      title={t('admin.clips.bulkUnpublishDialog.title', { count: clips.length })}
      sub={clips.length === 1 ? clips[0]?.title : t('admin.clips.bulkUnpublishDialog.subLive', { count: clips.length })}
      panelClass="max-w-md"
      footer={
        <>
          <Button onClick={onClose} disabled={running}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={running || clips.length === 0}>
            {running ? <Spinner className="w-3.5 h-3.5" /> : <Icon.eyeOff className="w-3.5 h-3.5" />}
            {t('admin.clips.bulkUnpublishDialog.unpublishButton', { count: clips.length })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px] text-ink-2">
        <p>{t('admin.clips.bulkUnpublishDialog.bodyMain')}</p>
        <div className="rounded-sm border border-line-2 bg-bg-2 p-3 text-[12.5px]">
          <ul className="flex flex-col gap-1 text-ink-2">
            {head.map((c) => (
              <li key={c.id} className="truncate">
                <span className="font-mono text-muted">{c.id.slice(0, 8)}…</span> · {c.title}
              </li>
            ))}
          </ul>
          {restCount > 0 && (
            <p className="mt-1.5 text-muted">{t('admin.clips.bulkUnpublishDialog.andMore', { count: restCount })}</p>
          )}
        </div>
      </div>
    </Modal>
  );
}
